import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { isMobile } from "@/lib/config";
import { useAuth } from "@/lib/hooks";

const tabs = [
  { to: "/dashboard", label: "Dashboard", web: false },
  { to: "/billing", label: "Billing", web: true },
  { to: "/profile", label: "Profile", web: false },
] as const;

/** Account area: ink header with the member's name and tab links. */
export function AccountShell({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  const { user } = useAuth();
  return (
    <>
      <section className="surface-ink">
        <div className="mx-auto max-w-6xl px-5 pt-14">
          <p className="eyebrow text-gold">{user ? user.fullName : "Your account"}</p>
          <h1 className="mt-3 text-4xl text-ink-foreground md:text-5xl">{title}</h1>
          <div className="hairline-gold mt-5" />
          {subtitle && <p className="mt-5 max-w-2xl text-ink-foreground/70">{subtitle}</p>}
          <nav className="mt-10 flex gap-6 overflow-x-auto">
            {tabs
              .filter((t) => !(t.web && isMobile))
              .map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  className="border-b-2 border-transparent pb-3 text-sm text-ink-foreground/60 transition-colors hover:text-ink-foreground"
                  activeProps={{ className: "!border-gold !text-ink-foreground font-medium" }}
                >
                  {t.label}
                </Link>
              ))}
          </nav>
        </div>
      </section>
      <div className="mx-auto max-w-6xl px-5 py-12">{children}</div>
    </>
  );
}

export function Panel({ title, action, children, className }: { title?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border border-border bg-card p-7 shadow-card ${className ?? ""}`}>
      {(title || action) && (
        <div className="mb-5 flex items-center justify-between gap-4">
          {title && <h2 className="text-2xl">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}
